// ═══ THE WORD STORE — local-first, the way Ink keeps its notes ══════════════
// THE LOCAL COPY IS THE TRUTH FOR THE SESSION. Every edit lands here first,
// synchronously, and the graph is told afterwards. Nothing that comes back
// from the wire may overwrite a field the hand has touched since the request
// left: each edit stamps the field with a revision, each request remembers
// the revisions it carried, and a response is applied field by field only
// where nothing newer stands.
//
// IDS ARE STABLE. A new word is born with a local id the moment the page
// opens (local_…), exactly as an Ink note is; it is created on the wire once
// it has a name, and the real id is swapped in everywhere — the row, the
// bookkeeping, the route — without the page ever changing under the hand.
// The slug is cosmetic and may change with every keystroke; nothing is
// addressed by it.
//
// THE WIRE IS COALESCED, never debounced: one request in flight per word,
// the latest fields waiting behind it. Ten keystrokes cost at most two
// requests, and the last one always carries the final words.
import { useCallback, useRef, useState } from 'react'
import { api } from './api.js'
import { applyTagOp } from '../../lib/move.mjs'

// one collator, made once: localeCompare with options builds one per call,
// and a 1,500-word list sorts on every keystroke that renames (audit 2026-10-02)
const collate = new Intl.Collator(undefined, { sensitivity: 'base' }).compare
export const bySort = (ws, nameOf = w => w.name) => [...ws].sort((a, b) => collate(nameOf(a), nameOf(b)))
export const isLocal = id => String(id || '').startsWith('local_')
// (the custom properties and their row order too: an answer from the wire must
// not take back a property the hand has just minted or typed into)
const EDITABLE = ['name', 'body', 'text', 'tags', 'aliases', 'status', 'stance', 'rather', 'replacement', 'properties', 'property_order']
// the links are never edited here: they are written both ways on the server
// (api.link / api.unlink), and arrive as the two words they changed
const LINKED = ['synonyms', 'antonyms']
const blank = id => ({
  id, _page: id, _local: true, slug: '', name: '', body: '', text: '', status: 'committed', tags: [], aliases: [], synonyms: [], antonyms: [], stance: 'held', rather: [], replacement: null,
  createdAt: null, updatedAt: null, editedAt: null, committedAt: null, committedAs: '', versions: [], usedIn: [], usages: [], legacy: false,
})

export function useWordStore() {
  /* THE LIST YOU HAD, BEFORE THE NETWORK SAYS A WORD (owner report
     2026-09-19: "why doesn't the app have local storage or some persistence,
     so refreshing doesn't cause this clunk?"). Ink paints its list from a
     stored copy and revalidates; so does this now. The copy is per SPACE — the
     address's first segment is the space's slug — and it is a cache, never the
     truth: the server's answer replaces it within the second, and nothing is
     ever written from it. */
  const SHELF = `dictionary:words:${(typeof location !== 'undefined' && location.pathname.split('/')[1]) || 'personal'}`
  const [words, setWordsState] = useState(() => {
    try { const raw = localStorage.getItem(SHELF); const list = raw ? JSON.parse(raw) : null; return Array.isArray(list) ? list.map(w => ({ ...w, _page: w.id })) : [] }
    catch { return [] }
  })
  // the copy is written whenever the server's list lands
  const shelve = list => { try { localStorage.setItem(SHELF, JSON.stringify(list.filter(w => !isLocal(w.id)).map(({ _page, _local, ...w }) => w))) } catch { /* a quota failure is not a fault worth a word */ } }
  const [error, setError] = useState('')
  const wordsRef = useRef([])
  const setWords = useCallback(f => setWordsState(ws => { const n = typeof f === 'function' ? f(ws) : f; wordsRef.current = n; return n }), [])
  const rev = useRef(0)
  const dirty = useRef(new Map())    // id -> { field: rev of the last local edit }
  const sync = useRef(new Map())     // id -> { pending: fields, inflight: bool }
  const swaps = useRef(new Map())    // local id -> real id
  const onSwap = useRef(new Set())   // (localId, realId) => {}
  const realOf = id => swaps.current.get(id) || id
  const find = id => wordsRef.current.find(w => w.id === id)

  /* apply a server copy, keeping every field the hand has touched since the request left */
  const linkedAt = useRef(new Map())   // id -> when its links last came back from a link write
  const applyServer = (id, srv, sentRevs, sentAt = 0) => {
    const d = dirty.current.get(id) || {}
    // an answer to a request that left before the last link write carries the old links
    const stale = (linkedAt.current.get(id) || 0) > sentAt
    setWords(ws => ws.map(w => {
      if (w.id !== id) return w
      // a friend's word keeps its owner as the row knows them — a write's
      // answer may name them less fully than the list did
      const out = { ...srv, id, _page: w._page, _local: false, usedIn: w.usedIn && w.usedIn.length ? w.usedIn : (srv.usedIn || []),
        ...(w.owner ? { owner: { ...srv.owner, ...w.owner } } : {}) }
      for (const f of EDITABLE) if (d[f] !== undefined && d[f] > (sentRevs[f] ?? -1)) out[f] = w[f]
      if (stale) for (const f of LINKED) out[f] = w[f]
      return out
    }))
    for (const f of Object.keys(d)) if (d[f] <= (sentRevs[f] ?? -1)) delete d[f]
    if (!Object.keys(d).length) dirty.current.delete(id)
  }

  const flush = async id => {
    const s = sync.current.get(id)
    if (!s || s.inflight || !Object.keys(s.pending).length) return
    const w = find(id)
    if (!w) { sync.current.delete(id); return }
    const fields = s.pending; s.pending = {}; s.inflight = true
    const d = dirty.current.get(id) || {}
    const sentRevs = {}, sentAt = Date.now()
    try {
      if (isLocal(id)) {
        // BORN AT ONCE, UNTITLED (Ink's model): the record is created the moment
        // the page opens, before it has a name, and every keystroke after is an
        // update on a stable id. Never a create by a partial name — that minted
        // 'death' on the way to 'deathness' — and never the adoption of some
        // other entry that happens to share the letters typed so far.
        for (const f of EDITABLE) sentRevs[f] = d[f] ?? rev.current
        const srv = (await api.create({ name: w.name, body: w.body || '', text: w.text || '', status: w.status, tags: w.tags, aliases: w.aliases, stance: w.stance, rather: w.rather, replacement: w.replacement })).word
        const real = srv.id
        swaps.current.set(id, real)
        if (dirty.current.has(id)) { dirty.current.set(real, dirty.current.get(id)); dirty.current.delete(id) }
        sync.current.set(real, { pending: s.pending, inflight: false }); sync.current.delete(id)
        setWords(ws => ws.map(x => x.id === id ? { ...x, id: real } : x))
        applyServer(real, srv, sentRevs)
        onSwap.current.forEach(fn => fn(id, real))
        // anything typed while the mint was in flight
        const t = sync.current.get(real)
        if (t && Object.keys(t.pending).length) flush(real)
        return
      }
      for (const f of Object.keys(fields)) sentRevs[f] = d[f] ?? rev.current
      // a friend's word is written through their perspective — the same
      // rule, the owner's door
      const r = w.owner ? await api.updateShared(w.owner.user_id, id, fields) : await api.update(id, fields)
      applyServer(id, r.word, sentRevs, sentAt)
    } catch (e) {
      // THE GRANT SAID NO. A friend's word that answers 403 is read-only for
      // you whatever the list believed: the row turns, the edit is dropped —
      // it could never land — and the page says whose word it is.
      if (w.owner && e.status === 403) {
        dirty.current.delete(id); sync.current.delete(id)
        setError(`${w.owner.display_name || w.owner.username || 'This'}'s word is read-only for you.`)
        // their copy first, THEN the verb: the page is re-keyed on the verb,
        // and it must remount on their words, not on the ones just refused
        await loadShared()
        setWords(ws => ws.map(x => x.id === id ? { ...x, access: 'view' } : x))
        return
      }
      setError(e.message)
      // the edit stays local and dirty; it goes out with the next change
      const t = sync.current.get(realOf(id)); if (t) t.pending = { ...fields, ...t.pending }
    } finally {
      const t = sync.current.get(realOf(id))
      if (t) { t.inflight = false; if (Object.keys(t.pending).length) flush(realOf(id)) }
    }
  }

  /* an edit: local first, stamped, then queued */
  const edit = (id, fields) => {
    id = realOf(id)
    const w = find(id); if (!w) return
    rev.current += 1
    const d = dirty.current.get(id) || {}
    for (const f of Object.keys(fields)) d[f] = rev.current
    dirty.current.set(id, d)
    // only a new name can move a word in the order; anything else edits in place
    setWords(ws => { const next = ws.map(x => x.id === id ? { ...x, ...fields } : x); return 'name' in fields ? bySort(next) : next })
    // an emptied title is a name like any other: the word stands, untitled,
    // out of the list until it has a name again (sweep() clears it if left)
    const s = sync.current.get(id) || { pending: {}, inflight: false }
    s.pending = { ...s.pending, ...fields }
    sync.current.set(id, s)
    flush(id)
  }

  const create = (seed = {}) => {
    const id = 'local_' + crypto.randomUUID()
    // a word born inside a dictionary wears its tag; inside an anti-dictionary, its stance too
    setWords(ws => bySort([...ws, { ...blank(id), ...seed }]))
    // on the wire at once, untitled
    sync.current.set(id, { pending: { name: '', ...seed }, inflight: false })
    queueMicrotask(() => flush(id))
    return id
  }
  /* an untitled word with nothing written, left behind, is not kept */
  const sweep = id => {
    id = realOf(id)
    const w = find(id); if (!w || w.owner) return
    if (!w.name.trim() && !(w.text || '').trim()) remove(id)
  }
  // A FRIEND'S WORD IS NOT YOURS TO REMOVE, COMMIT OR RE-SHARE — the two verbs
  // a grant can carry are an edit and a stamp; every other verb below refuses
  // an owner row rather than sending a write the platform would refuse anyway
  const remove = id => {
    id = realOf(id)
    const w = find(id); if (w && w.owner) return
    // the server takes it off every word that listed it; the list does the same at once
    const off = x => (LINKED.some(f => (x[f] || []).some(r => r.id === id && !r.group && !r.user))
      ? { ...x, ...Object.fromEntries(LINKED.map(f => [f, (x[f] || []).filter(r => !(r.id === id && !r.group && !r.user))])) } : x)
    setWords(ws => ws.filter(x => x.id !== id).map(off))
    dirty.current.delete(id)
    if (!isLocal(id)) { sync.current.delete(id); api.remove(id).catch(e => { setError(e.message); load() }); return }
    // still being created: delete it the moment the wire answers
    const s = sync.current.get(id)
    if (s && s.inflight) { const un = onSwap.current; const fn = (from, to) => { if (from === id) { un.delete(fn); api.remove(to).catch(() => {}); setWords(ws => ws.filter(x => x.id !== to)) } }; un.add(fn) }
    else sync.current.delete(id)
  }
  // A STAMP IS THE SERVER'S TO PLACE — it needs your name and the node's rev
  // as the graph holds it, so there is no optimistic half: the word comes back
  // with its stamps and stands in for what was there.
  // a stamp on a friend's word goes through their perspective; a 403 says the
  // grant does not reach that far, and the row turns read-only
  const stampWith = async (id, own, shared) => {
    id = realOf(id); if (isLocal(id)) return
    const w = find(id); if (!w) return
    try {
      const r = w.owner ? await shared(w.owner.user_id, id) : await own(id)
      applyServer(id, r.word, {})
    } catch (e) {
      if (w.owner && e.status === 403) { setError(`${w.owner.display_name || w.owner.username || 'This'}'s word is read-only for you.`); await loadShared(); setWords(ws => ws.map(x => x.id === id ? { ...x, access: 'view' } : x)); return }
      setError(e.message); w.owner ? loadShared() : load()
    }
  }
  const stamp = id => stampWith(id, api.stamp, api.stampShared)
  const unstamp = id => stampWith(id, api.unstamp, api.unstampShared)
  const commit = async id => {
    id = realOf(id)
    const w = find(id); if (w && w.owner) return
    if (isLocal(id)) return edit(id, { status: 'committed' })
    rev.current += 1
    const d = dirty.current.get(id) || {}; d.status = rev.current; dirty.current.set(id, d)
    const sent = { status: rev.current }
    setWords(ws => ws.map(x => x.id === id ? { ...x, status: 'committed' } : x))
    try { const r = await api.commit(id); applyServer(id, r.word, sent) } catch (e) { setError(e.message); load() }
  }
  /* A COPY GETS A NAME NOBODY HOLDS (owner 2026-09-19: "the duplicate button
     doesn't do shit"): "x copy", then "x copy 2", "x copy 3" — a second
     duplicate used to 409 on the name and die without a word. And it THROWS
     on failure now rather than swallowing it into the banner: the caller is
     the one standing next to the hand that pressed, and says so there. */
  const duplicate = async w => {
    const base = w.name.replace(/ copy( \d+)?$/, '')
    const taken = new Set(words.map(x => x.name.trim().toLowerCase()))
    let name = `${base} copy`
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = `${base} copy ${n}`
    try {
      const r = await api.create({ name, body: w.body || '', text: w.text || '', status: w.status, tags: w.tags, stance: w.stance, rather: w.rather })
      setWords(ws => bySort([...ws, { ...r.word, _page: r.word.id }]))
      return r.word
    } catch (e) {
      const msg = e.status === 409 ? `"${name}" already exists` : e.status === 401 || e.status === 403 ? 'the app is not authorized to write — sign in again' : e.message
      setError(msg); throw new Error(msg)
    }
  }

  /* MANY WORDS, ONE TAG OP EACH — the move between dictionaries (owner ask
     2026-10-03; the ops and what they mean are lib/move.mjs's). The same law
     as an edit: local first, stamped with a revision, the wire after — but as
     ONE batch, and with an answer per word, because a move of thirty words
     that half-landed must say which half. A word whose op the graph refused is
     put back as it stood (unless the hand has touched its tags since), and is
     named in `failed`; if the batch itself got no answer, what landed is not
     known, so the list is read back from the graph rather than guessed. A
     word with a write still in flight is waited for first, or that write
     could land after the move and undo it. Resolves to
     { done: [{ id, op, before }], failed: [{ id, status, error }], skipped: [{ id, reason }] }. */
  const settle = async id => {
    for (let i = 0; i < 50; i++) {   // ten seconds at most; a write stuck longer is the banner's to report
      const s = sync.current.get(realOf(id))
      if (!s || !s.inflight) return
      await new Promise(r => setTimeout(r, 200))
    }
  }
  const retag = async (ops, { chunk = 100 } = {}) => {
    const out = { done: [], failed: [], skipped: [] }
    const go = []
    for (const raw of ops || []) {
      const id = realOf(raw.id), w = find(id)
      if (!w) out.skipped.push({ id, reason: 'gone' })
      else if (w.owner) out.skipped.push({ id, reason: 'not yours' })
      else if (isLocal(id)) out.skipped.push({ id, reason: 'not saved yet' })
      else go.push({ ...raw, id })
    }
    if (!go.length) return out
    await Promise.all(go.map(op => settle(op.id)))
    // local first: every word moves at once, under one revision
    rev.current += 1
    const r = rev.current, marks = new Map(), prev = new Map(), byId = new Map(go.map(op => [op.id, op]))
    for (const op of go) {
      const w = find(op.id)
      prev.set(op.id, { tags: w.tags || [], stance: w.stance })
      const mark = { tags: r, ...(op.stance ? { stance: r } : {}) }
      marks.set(op.id, mark)
      dirty.current.set(op.id, { ...(dirty.current.get(op.id) || {}), ...mark })
      // a tag edit still waiting to go out would carry the old list over the move
      const s = sync.current.get(op.id)
      if (s && Array.isArray(s.pending.tags)) s.pending.tags = applyTagOp(s.pending.tags, op)
      if (s && op.stance && 'stance' in s.pending) s.pending.stance = op.stance
    }
    setWords(ws => ws.map(w => byId.has(w.id) ? { ...w, tags: applyTagOp(w.tags, byId.get(w.id)), ...(byId.get(w.id).stance ? { stance: byId.get(w.id).stance } : {}) } : w))
    // put one word back as it stood, where nothing newer stands on it
    const putBack = id => {
      const d = dirty.current.get(id) || {}, m = marks.get(id), was = prev.get(id)
      const back = {}
      if (d.tags === m.tags) { back.tags = was.tags; delete d.tags }
      if (m.stance && d.stance === m.stance) { back.stance = was.stance; delete d.stance }
      if (!Object.keys(d).length) dirty.current.delete(id)
      if (Object.keys(back).length) setWords(ws => ws.map(w => w.id === id ? { ...w, ...back } : w))
    }
    let unsure = false
    for (let i = 0; i < go.length; i += chunk) {
      const part = go.slice(i, i + chunk)
      let results
      try {
        results = (await api.retag(part.map(({ id, add, remove, stance }) => ({ id, add, remove, ...(stance ? { stance } : {}) })))).results || []
      } catch (e) {
        unsure = unsure || !e.status || e.status >= 500
        results = part.map(op => ({ id: op.id, ok: false, status: e.status || 0, error: e.message }))
      }
      for (const op of part) {
        const res = results.find(x => x && x.id === op.id) || { ok: false, status: 0, error: 'no answer for this word' }
        if (res.ok && res.word) {
          applyServer(op.id, res.word, marks.get(op.id))
          out.done.push({ id: op.id, op, before: res.before || prev.get(op.id) })
        } else {
          putBack(op.id)
          out.failed.push({ id: op.id, status: res.status || 0, error: res.error || 'could not be written' })
        }
      }
    }
    // the batch got no answer: what landed is the graph's to say
    if (unsure) await load()
    return out
  }
  /* A LINK IS TWO WORDS (owner call 2026-10-04). No optimistic half: the
     server writes both sides (and may mint the other word), and the answer is
     both words as they now stand — each put in the list, the new one added.
     Throws, so the hand that pressed hears why. */
  const putLinked = list => {
    const at = Date.now()
    setWords(ws => {
      let next = ws
      for (const srv of list) {
        if (!srv || !srv.id) continue
        linkedAt.current.set(srv.id, at)
        const have = next.find(x => x.id === srv.id)
        next = have ? next.map(x => x.id === srv.id ? { ...x, ...Object.fromEntries(LINKED.map(f => [f, srv[f] || []])), rev: srv.rev, commitments: srv.commitments } : x)
          : [...next, { ...srv, _page: srv.id }]
      }
      return bySort(next)
    })
  }
  const link = async (id, fields) => {
    id = realOf(id); if (isLocal(id)) throw new Error('the word is not saved yet')
    try {
      const r = await api.link(id, fields)
      // a word minted in another dictionary's graph is not this list's to hold
      putLinked(r.elsewhere ? [r.word] : [r.word, r.target])
      return r
    } catch (e) { setError(e.message); throw e }
  }
  const unlink = async (id, ref) => {
    id = realOf(id)
    try { const r = await api.unlink(id, ref); putLinked([r.word, r.target].filter(Boolean)); return r }
    catch (e) { setError(e.message); throw e }
  }

  // the words as they stand right now, for a verb that runs after an await
  const current = () => wordsRef.current

  /* a reload MERGES: the server's copy of every word, except the fields still
     dirty here and the local words the graph has not heard of */
  const load = useCallback(async () => {
    try {
      const r = await api.words()
      setWords(ws => {
        const fresh = (r.words || []).map(srv => {
          const mine = ws.find(x => x.id === srv.id)
          if (!mine) return { ...srv, _page: srv.id }
          const d = dirty.current.get(srv.id) || {}
          const out = { ...srv, _page: mine._page }
          for (const f of Object.keys(d)) out[f] = mine[f]
          return out
        })
        const known = new Set(fresh.map(w => w.id))
        // a friend's words stand where they are — theirs is a different read
        const next = bySort([...fresh, ...ws.filter(w => w.owner || isLocal(w.id) || (!known.has(w.id) && dirty.current.has(w.id)))])
        shelve(next)
        return next
      })
      setError('')
    } catch (e) {
      // THE LIST YOU HAD STANDS. A read that fails leaves the words already on
      // screen where they are and names the fault; it never replaces a
      // dictionary with an empty one (owner report 2026-09-19).
      setError(e.message === 'Bad Gateway' ? 'The dictionary could not be reached just now.' : e.message)
    }
  }, [setWords])

  /* WHAT FRIENDS SHARED WITH YOU (2026-09-19): the other read. Every owner
     row is replaced by the server's answer — a friend's word is never dirty
     here for long, and never local — and a failed read leaves the ones
     standing where they are, silently: a friend who shares nothing is not a
     fault worth a word. */
  const loadShared = useCallback(async () => {
    try {
      const r = await api.incoming()
      setWords(ws => {
        const theirs = (r.words || []).map(srv => {
          // an edit in flight on a friend's word keeps the fields the hand holds
          const mine = ws.find(x => x.id === srv.id)
          const out = { ...srv, _page: srv.id }
          for (const f of Object.keys(mine ? (dirty.current.get(srv.id) || {}) : {})) out[f] = mine[f]
          return out
        })
        const next = bySort([...ws.filter(w => !w.owner), ...theirs])
        shelve(next)
        return next
      })
    } catch { /* the list you had stands */ }
  }, [setWords])

  return { words, setWords, error, setError, load, loadShared, create, edit, remove, commit, stamp, unstamp, duplicate, sweep, realOf, retag, current, link, unlink, onSwap: fn => { onSwap.current.add(fn); return () => onSwap.current.delete(fn) } }
}
